import { Injectable, NgZone } from '@angular/core';
import { GitResource, GitStatus, VsCodeMessageTypeEnum } from '@goes-funky/y42-vscode';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GitEventType, GitQuery, GitService } from '@y42/git';
import { GitStatusToStatusRow } from '@y42/git/worker';
import { formatByPrettier, MonacoEditorLoader } from '@y42/monaco-editor';
import { OrgSpaceService } from '@y42/org-space';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { OrgUserQuery } from '@y42/org-user/state';
import { UserService } from '@y42/user';
import { UserQuery } from '@y42/user/state';
import { assertNonNullable } from '@y42/util/assert';
import { rgba2hex } from '@y42/util/rgba2hex';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  fromEvent,
  lastValueFrom,
  map,
  merge,
  Observable,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { VSCODE_SETTINGS } from './vscode-settings';

export const ROOT_FOLDER = 'src';

function isGitFolder(path: string) {
  return path.includes('/.git/') || path.endsWith('/.git');
}

function normalizedPath(path: string): string {
  return `${ROOT_FOLDER}${path}`;
}

export interface VsCodeEditorEvent {
  id: string;
  payload: {
    type: string;
    [key: string]: string;
  };
}

export const StatusRowToGitStatus = {
  [GitStatusToStatusRow['M '].join()]: GitStatus.IndexModified,
  [GitStatusToStatusRow['A '].join()]: GitStatus.IndexAdded,
  [GitStatusToStatusRow['D '].join()]: GitStatus.IndexDeleted,

  [GitStatusToStatusRow[' M'].join()]: GitStatus.Modified,
  [GitStatusToStatusRow[' D'].join()]: GitStatus.Deleted,

  [GitStatusToStatusRow['??'].join()]: GitStatus.Untracked,
};

@UntilDestroy()
@Injectable()
export class VsCodeEditorService {
  constructor(
    private readonly ngZone: NgZone,
    private readonly gitService: GitService,
    private readonly gitQuery: GitQuery,
    private readonly orgSpaceQuery: OrgSpaceQuery,
    private readonly orgSpaceService: OrgSpaceService,
    private readonly orgUserQuery: OrgUserQuery,
    private readonly userService: UserService,
    private readonly userQuery: UserQuery,
    private readonly monacoLoader: MonacoEditorLoader,
  ) {
    this.gitService.filesChanged$
      .pipe(
        tap(async (paths) => {
          const changes = await Promise.all(
            paths.map(async (path) => {
              let content = null;
              const isDir = this.gitQuery.isDir(path);
              try {
                content = isDir ? '' : await this.gitService.readFile(path);
              } catch (e) {
                // doesn't exist
              }

              return {
                path,
                isDir,
                ...(content !== null ? { content } : {}),
              };
            }),
          );
          this.sendMessageToIframe({
            type: VsCodeMessageTypeEnum.ChannelHost,
            payload: {
              type: VsCodeMessageTypeEnum.FileChanges,
              payload: changes,
            },
          });
        }),
        untilDestroyed(this),
      )
      .subscribe();
    this.ngZone.runOutsideAngular(() => {
      combineLatest([fromEvent<MessageEvent>(window, 'message'), this.iframe$$.pipe(filter(Boolean))])
        .pipe(
          map(([e]) => e),
          filter((e) => e.source === this.iframe?.contentWindow),
          switchMap((e) => this.handleIframeMessages(e).catch((err: unknown) => console.log(err))),
          untilDestroyed(this),
        )
        .subscribe();
    });
  }

  readonly url$ = merge(
    this.orgSpaceQuery.active$,
    this.gitQuery.listen(GitEventType.BranchChanged),
    this.gitQuery.listen(GitEventType.BranchMerged),
    this.gitQuery.listen(GitEventType.BranchPulled),
    this.gitQuery.listen(GitEventType.ChangesStashed),
    this.gitQuery.listen(GitEventType.ChangesRestored),
    this.gitQuery.listen(GitEventType.ChangesDiscarded),
    this.gitQuery.listen(GitEventType.ChangesReverted),
  ).pipe(
    debounceTime(100),
    map(() => `/vscode/index.html?path=/&query=${this.vsCodeWorkspaceIdentifier}`),
  );

  private readonly loading$$ = new BehaviorSubject(true);
  readonly loading$: Observable<boolean> = this.loading$$;

  private options: {
    pathToOpen: string;
    propertyPathToOpen: string;
    completionConfig: Partial<typeof monaco.languages.sql.completionConfig>;
  } = {
    pathToOpen: '',
    propertyPathToOpen: '',
    completionConfig: {},
  };

  private readonly activeFile$$ = new Subject<string>();
  readonly activeFile$: Observable<string> = this.activeFile$$.pipe(map((path) => normalizedPath(path)));

  private readonly activeFileSaved$$ = new Subject<string>();
  readonly activeFileSaved$: Observable<string> = this.activeFileSaved$$;

  private readonly activeYmlPropertyPath$$ = new Subject<string | null>();
  readonly activeYmlPropertyPath$ = this.activeYmlPropertyPath$$.asObservable();

  private readonly ctrlKClicked$$ = new Subject<void>();
  readonly ctrlKClicked$ = this.ctrlKClicked$$.asObservable();

  private readonly completeMerge$$ = new Subject<string>();
  readonly completeMerge$ = this.completeMerge$$.asObservable();

  private readonly iframe$$ = new BehaviorSubject<HTMLIFrameElement | null>(null);
  private get iframe() {
    return this.iframe$$.value;
  }

  initialize(options: {
    iframe: HTMLIFrameElement;
    pathToOpen: string;
    propertyPathToOpen: string;
    openMergeEditor: boolean;
    completionConfig: Partial<typeof monaco.languages.sql.completionConfig>;
  }) {
    this.iframe$$.next(options.iframe);
    this.options = options;
  }

  openPath(path: string) {
    if (!this.iframe?.contentWindow) {
      return;
    }
    this.sendMessageToIframe({
      type: VsCodeMessageTypeEnum.ChannelHost,
      payload: {
        type: VsCodeMessageTypeEnum.OpenPathRequested,
        payload: path,
      },
    });
  }

  // 'query' is used for generating unique vscode workspace
  get vsCodeWorkspaceIdentifier() {
    const activeSpace = this.orgSpaceQuery.getActive();
    const activeBranch = this.gitQuery.getActiveBranchName();
    return activeSpace && activeBranch ? `${activeSpace.gitProvider}/${activeSpace.repoFullName}/${activeBranch}` : '';
  }

  private async handleIframeMessages(e: MessageEvent) {
    if (e.data.type === VsCodeMessageTypeEnum.Init) {
      if (this.iframe) {
        this.applyCustomStyles(this.iframe);
      }
      const configurationDefaults = await this.initializeConfigurationDefaults();
      this.sendMessageToIframe({
        type: e.data.type,
        payload: {
          configurationDefaults,
        },
      });
    } else if (e.data?.id) {
      return this.handleIframeChannelMessages(e.data);
    }
  }

  private listenToGitChanges() {
    this.gitQuery.modifiedFilesList$
      .pipe(
        switchMap(() => this.gitService.getFileChanges()),
        tap((changes) => {
          const resources: GitResource[] = changes.map(({ path, status, original }) => ({
            path,
            status: StatusRowToGitStatus[status.join()],
            originalContent: original || '',
          }));
          this.sendMessageToIframe({
            type: VsCodeMessageTypeEnum.ChannelHost,
            payload: {
              type: VsCodeMessageTypeEnum.ModifiedListChanged,
              payload: resources,
            },
          });
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private async handleIframeChannelMessages(data: VsCodeEditorEvent) {
    const sendBack = (payload: unknown) => {
      this.sendMessageToIframe({
        type: VsCodeMessageTypeEnum.ChannelHost,
        payload: {
          id: data.id,
          payload,
        },
      });
    };

    switch (data.payload.type) {
      case VsCodeMessageTypeEnum.FsInit: {
        const tree = await this.gitService.readFiles(ROOT_FOLDER);
        const { pathToOpen, propertyPathToOpen, completionConfig } = this.options;
        const conflicts = await this.gitService.getFileConflicts();

        sendBack({
          tree,
          pathToOpen: `${pathToOpen}?${this.vsCodeWorkspaceIdentifier}`,
          propertyPathToOpen,
          completionConfig,
          conflicts: conflicts.length ? conflicts : null,
        });
        this.listenToGitChanges();
        this.ngZone.run(() => {
          this.loading$$.next(false);
        });
        break;
      }
      case VsCodeMessageTypeEnum.FsReadContent: {
        if (!data.payload.fullPath) {
          return;
        }
        let content = '';
        try {
          content = await this.gitService.readFile(normalizedPath(data.payload.fullPath));
        } catch (e) {
          // file doesn't exist
        }
        sendBack(content);
        break;
      }
      case VsCodeMessageTypeEnum.GetBlameLines: {
        if (!data.payload.fullPath) {
          return;
        }
        const orgSpaceId = this.orgSpaceQuery.getActiveId();
        if (orgSpaceId) {
          const repoUrl = this.orgSpaceService.getGitProviderPublicRepositoryUrl(orgSpaceId);
          const lines = await this.gitService.getBlameLines({
            path: normalizedPath(data.payload.fullPath),
            contents: data.payload.content,
          });

          sendBack(lines.map((blameLine) => ({ ...blameLine, commitUrl: `${repoUrl}/-/commit/${blameLine.sha}` })));
        }

        break;
      }
      case VsCodeMessageTypeEnum.FsWriteContent: {
        if (!data.payload.fullPath || isGitFolder(data.payload.fullPath)) {
          return;
        }
        let content = data.payload.content;
        if (data.payload.fullPath.endsWith('.json')) {
          content = JSON.parse(content || 'null');
        }
        await this.gitService.scheduleWriteFile(
          {
            path: normalizedPath(data.payload.fullPath),
            content,
          },
          false,
        );
        this.activeFileSaved$$.next(data.payload.fullPath);
        break;
      }
      case VsCodeMessageTypeEnum.FsCreateFolder: {
        if (!data.payload.fullPath || isGitFolder(data.payload.fullPath)) {
          return;
        }
        this.gitService.createFolder(normalizedPath(data.payload.fullPath), {
          skipSync: false,
          emitFilesChangedEvent: false,
        });
        break;
      }

      case VsCodeMessageTypeEnum.FsCreateFile: {
        if (!data.payload.fullPath || isGitFolder(data.payload.fullPath)) {
          return;
        }
        this.gitService.writeFile(
          {
            path: normalizedPath(data.payload.fullPath),
            content: '',
          },
          {
            skipSetStatus: false,
            emitFilesChangedEvent: false,
          },
        );
        break;
      }

      case VsCodeMessageTypeEnum.FsRename: {
        if (!data.payload.oldPath || !data.payload.newPath) {
          return;
        }
        const newName = data.payload.newPath.split('/').pop();
        if (newName && !isGitFolder(newName)) {
          this.gitService.renameEntry(
            {
              oldPath: normalizedPath(data.payload.oldPath),
              newPath: normalizedPath(data.payload.newPath),
            },
            { skipUIUpdate: false, emitFilesChangedEvent: false },
          );
        }

        break;
      }

      case VsCodeMessageTypeEnum.FsDeleteFile: {
        if (!data.payload.fullPath) {
          return;
        }
        this.gitService.bulkDelete(
          [
            {
              path: normalizedPath(data.payload.fullPath),
              type: 'file',
            },
          ],
          false,
        );
        break;
      }

      case VsCodeMessageTypeEnum.FsDeleteFolder: {
        if (!data.payload.fullPath) {
          return;
        }
        this.gitService.bulkDelete(
          [
            {
              path: normalizedPath(data.payload.fullPath),
              type: 'dir',
            },
          ],
          false,
        );
        break;
      }

      case VsCodeMessageTypeEnum.FsActiveFileChanged: {
        if (!data.payload.fullPath) {
          return;
        }
        this.activeFile$$.next(data.payload.fullPath);
        break;
      }

      case VsCodeMessageTypeEnum.FormatSql: {
        if (!data.payload.payload) {
          return;
        }
        await this.monacoLoader.load();
        const { SqlPlugin } = await import('@y42/sql-prettier');
        const { value } = await formatByPrettier({
          text: data.payload.payload,
          lang: 'sql',
          plugins: [SqlPlugin],
        });
        sendBack(value);
        break;
      }
      case VsCodeMessageTypeEnum.CompleteMerge: {
        if (!data.payload.fullPath) {
          return;
        }
        const path = normalizedPath(data.payload.fullPath);
        await this.gitService.completeMerge(path);
        const conflicts = await this.gitService.getFileConflicts();
        sendBack(conflicts);
        this.completeMerge$$.next(path);
        break;
      }
      case VsCodeMessageTypeEnum.ExtractYmlName: {
        const propertyPath = data.payload.payload;
        console.log('ExtractYmlName', propertyPath);
        this.activeYmlPropertyPath$$.next(propertyPath ?? null);
        break;
      }
      case VsCodeMessageTypeEnum.CtrlKClicked: {
        this.ctrlKClicked$$.next();
        break;
      }
      case VsCodeMessageTypeEnum.ReadSettings: {
        const settings = await lastValueFrom(this.userService.loadVsCodeSettings());
        if (!settings || Array.isArray(settings)) {
          sendBack(`{}`);
          return;
        }
        sendBack(JSON.stringify(settings, null, 2));
        break;
      }
      case VsCodeMessageTypeEnum.WriteSettings: {
        this.userService
          .updateVsCodeSettings(data.payload.content)
          .pipe(tap(() => sendBack(true)))
          .subscribe();
        break;
      }
      case VsCodeMessageTypeEnum.DiscardChanges: {
        if (data.payload.payload) {
          const fullPath = normalizedPath(data.payload.payload);
          this.gitService.discardChanges([fullPath]);
        }

        break;
      }
      case VsCodeMessageTypeEnum.GetLog: {
        if (data.payload.path) {
          const fullPath = normalizedPath(data.payload.path);
          const offset = Number(data.payload.offset);
          const limit = Number(data.payload.limit);
          const history = await this.gitService.getHistory(offset, limit, fullPath);

          const commits = history.map((commit) => {
            const user = this.orgUserQuery.getUIEntity(commit.authorEmail);
            const userPic =
              user?.profile_pic || `${document.location.origin}/assets/images/y42-logos/y42-short-logo.svg`;

            return {
              userPic,
              ...commit,
            };
          });
          sendBack(commits);
        }
        break;
      }

      case VsCodeMessageTypeEnum.GetDiff: {
        if (data.payload.path && data.payload.parentHash && data.payload.hash) {
          const fullPath = normalizedPath(data.payload.path);
          const diffs = await this.gitService.getDiff(data.payload.parentHash, data.payload.hash, fullPath);
          const diff = diffs.find((item) => item.path === fullPath);

          sendBack(diff);
        }

        break;
      }
    }
  }

  private sendMessageToIframe(message: { type: string; payload: unknown }) {
    const iframe = this.iframe;
    assertNonNullable(iframe?.contentWindow, 'Expected "iframe.contentWindow" to be defined');
    iframe.contentWindow.postMessage(message, document.location.origin);
  }

  private async initializeConfigurationDefaults() {
    const themeKey = 'workbench.colorTheme';
    const settings = await lastValueFrom(this.userService.loadVsCodeSettings());
    if (settings && !Array.isArray(settings) && !(settings as Record<string, unknown>)[themeKey]) {
      const activeUser = this.userQuery.getActiveUser();

      const bodyStyle = getComputedStyle(document.body);
      const bgColor = rgba2hex(bodyStyle.getPropertyValue('--background-secondary').trim());
      const textColor = rgba2hex(bodyStyle.getPropertyValue('--typography-secondary').trim());

      return {
        ...VSCODE_SETTINGS,
        [themeKey]: activeUser.app_theme === 'dark' ? 'Default Dark+' : 'Default Light+',
        'workbench.colorCustomizations': {
          ...(bgColor
            ? {
                'activityBar.background': bgColor,
                'statusBar.background': bgColor,
              }
            : {}),
          ...(textColor
            ? {
                'activityBar.foreground': textColor,
                'statusBar.foreground': textColor,
              }
            : {}),
        },
      };
    }

    return VSCODE_SETTINGS;
  }

  private applyCustomStyles(iframe: HTMLIFrameElement) {
    if (!iframe?.contentDocument) {
      return;
    }

    const customStyle = iframe.contentDocument.createElement('link');
    customStyle.href = `/vscode-styles.css`;
    customStyle.rel = 'stylesheet';
    iframe.contentDocument.head.appendChild(customStyle);
  }
}
